/**
 * /devhub/workflows — Workflows (operator) · REQ-001.91 §1.4 REFERENCE PANEL.
 *
 * The first panel rebuilt to the §1.4 interactivity bar: the REAL @carbon/ibm-products Datagrid
 * (OperatorDataGrid, hooks wired) + row-click → OperatorDetailTearsheet (view / edit / Send-to-AI)
 * + AILabel on the AI-touched affordance. Read-only data is registry-backed (workflow_registry.json);
 * every edit / AI verb is proposal-safe (an ActionFrame an operator approves — never a browser write,
 * never a paid model). g100, zero inline styles, $spacing breathing room.
 *
 * Carbon MCP evidence: AILabel/AILabelContent (@carbon/react v1.108.0, `decorator` pattern); Datagrid
 * hooks (ibm-products 2.91.0) — see OperatorDataGrid header ledger.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AILabel, AILabelContent, InlineNotification, Tag, TextArea } from '@carbon/react';
import { DevHubPanelPage } from '../components/devhub/DevHubPanelPage';
import { OperatorDataGrid } from '../components/devhub-kit/OperatorDataGrid';
import { OperatorDetailTearsheet, type DetailField } from '../components/devhub-kit/OperatorDetailTearsheet';
import { useEntitlements } from '@lib/persistence/useEntitlements';
import { useWorkflowRegistry } from '@lib/persistence/useWorkflowRegistry';
import type { OperatorColumn, OperatorDetailMode } from '@contracts/devhub-kit.contract';
import {
  WORKFLOW_PLANE_LABELS,
  WORKFLOW_PLANE_TAG,
  WORKFLOW_STATUS_LABELS,
  WORKFLOW_STATUS_TAG,
  WORKFLOW_TRIGGER_LABELS,
} from '@contracts/workflow-registry.contract';

export const Route = createFileRoute('/devhub/workflows')({ component: WorkflowsPage });

type WorkflowRow = ReturnType<typeof useWorkflowRegistry>['rows'][number];

/** AI affordance shown on the grid header — names the local model + the propose/approve contract. */
function WorkflowsAiAffordance() {
  return (
    <AILabel size="sm" autoAlign aria-label="About AI on the Workflow Registry">
      <AILabelContent>
        <p className="ai-label__heading">Local Granite · paid_model_called=false</p>
        <p>
          AI can <strong>Analyze</strong> a workflow (read-only) from its row. It proposes — it never
          edits or runs a workflow directly; your approval applies any change as an ActionFrame.
        </p>
      </AILabelContent>
    </AILabel>
  );
}

function WorkflowsPage() {
  const { registry, rows, isLive, isLoading } = useWorkflowRegistry();
  const { isElevated } = useEntitlements();
  const [selected, setSelected] = useState<WorkflowRow | null>(null);
  const [mode, setMode] = useState<OperatorDetailMode>('view');
  const [aiPrompt, setAiPrompt] = useState('');
  const [editNote, setEditNote] = useState('');

  const columns = useMemo<OperatorColumn<WorkflowRow>[]>(
    () => [
      { key: 'workflowId', header: 'Workflow', render: (r) => <code className="view-tile__label">{r.workflowId}</code> },
      { key: 'title', header: 'Title' },
      {
        key: 'plane',
        header: 'Plane',
        filterable: true,
        render: (r) => <Tag type={WORKFLOW_PLANE_TAG[r.plane]} size="sm">{WORKFLOW_PLANE_LABELS[r.plane]}</Tag>,
      },
      { key: 'trigger', header: 'Trigger', render: (r) => <>{WORKFLOW_TRIGGER_LABELS[r.trigger]}</> },
      {
        key: 'status',
        header: 'Status',
        isStatus: true,
        filterable: true,
        render: (r) => <Tag type={WORKFLOW_STATUS_TAG[r.status]} size="sm">{WORKFLOW_STATUS_LABELS[r.status]}</Tag>,
      },
      { key: 'requiredPbac', header: 'PBAC scope', editable: true, render: (r) => <>{r.requiredPbac ?? '—'}</> },
    ],
    []
  );

  const detailFields = useMemo<DetailField[]>(() => {
    if (!selected) return [];
    return [
      { label: 'Workflow id', value: <code>{selected.workflowId}</code> },
      { label: 'Title', value: selected.title },
      { label: 'Plane', value: WORKFLOW_PLANE_LABELS[selected.plane] },
      { label: 'Trigger', value: WORKFLOW_TRIGGER_LABELS[selected.trigger] },
      { label: 'Status', value: <Tag type={WORKFLOW_STATUS_TAG[selected.status]} size="sm">{WORKFLOW_STATUS_LABELS[selected.status]}</Tag> },
      { label: 'Scripts', value: selected.scripts.length ? selected.scripts.join(', ') : '—' },
      { label: 'Guardrails', value: selected.guardrails.length ? selected.guardrails.join(', ') : '—' },
      { label: 'ActionFrame required', value: selected.actionframeRequired ? 'Yes' : 'No' },
      { label: 'PBAC scope', value: selected.requiredPbac ?? '—' },
    ];
  }, [selected]);

  function openRow(row: WorkflowRow) {
    setSelected(row);
    setMode('view');
    setEditNote(row.requiredPbac ?? '');
    setAiPrompt('');
  }

  if (!isElevated) {
    return (
      <DevHubPanelPage title="Workflows" subtitle="Workflow registry and script runtime classification." status={{ label: 'restricted', live: false }}>
        <InlineNotification kind="info" lowContrast hideCloseButton title="Operator access required" subtitle="The Workflow Registry is available to Developer Hub operators only." />
      </DevHubPanelPage>
    );
  }

  return (
    <DevHubPanelPage
      title="Workflows"
      subtitle="Workflow registry and script runtime classification. Click a row for full detail, edit, or Send-to-AI."
      status={{ label: isLive ? 'live' : 'sample', live: isLive }}
      actions={<WorkflowsAiAffordance />}
    >
      <OperatorDataGrid<WorkflowRow>
        ariaLabel={`Workflow registry · ${rows.length} workflows · v${registry.version}`}
        columns={columns}
        rows={rows}
        getRowId={(r) => r.workflowId}
        onRowClick={openRow}
        loading={isLoading}
        emptyLabel="No workflows registered yet — they appear once registered in data/runtime/workflow_registry.json."
        expandedRowContent={(r) => (
          <span>
            {r.scripts.length} script(s) · {r.guardrails.length} guardrail(s) · ActionFrame {r.actionframeRequired ? 'required' : 'none'} · PBAC {r.requiredPbac ?? '—'}
          </span>
        )}
      />

      <OperatorDetailTearsheet
        open={selected != null}
        onClose={() => setSelected(null)}
        title={selected ? selected.title : ''}
        subtitle={selected ? selected.workflowId : undefined}
        mode={mode}
        onModeChange={setMode}
        fields={detailFields}
        editSlot={
          <TextArea
            id="wf-edit-pbac"
            labelText="Proposed PBAC scope"
            helperText="Submitting creates a reviewable ActionFrame proposal — no change is applied directly from your browser."
            value={editNote}
            onChange={(e) => setEditNote(e.currentTarget.value)}
          />
        }
        aiSlot={
          <TextArea
            id="wf-ai-analyze"
            labelText="Ask local Granite to analyze this workflow"
            helperText="Analyze is read-only (enqueues rag.chat_request). The answer is produced by local Granite; paid_model_called=false."
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.currentTarget.value)}
          />
        }
        primaryAction={
          mode === 'edit'
            ? { label: 'Submit proposal', onClick: () => setSelected(null), disabled: !editNote.trim() }
            : mode === 'send-to-ai'
              ? { label: 'Send to AI', onClick: () => setSelected(null), disabled: !aiPrompt.trim() }
              : undefined
        }
      />
    </DevHubPanelPage>
  );
}
